import React, { useEffect, useMemo, useState } from 'react'
import { lmsAdmin } from '../api'
import { useAuthContext } from '../../../context/auth.context'
import { LmsIcon } from '../lms-icon'

// Unlink everyone from one LMS (Phase F E1b, ruling F-2; the service's
// retire-system-key call). A source key names where a linked account came from: an LMS's own (lms:<kind>:<issuer>, shown in
// the links list), or a partner's slug. The service retires only a key whose
// links the LMS manages: an LMS's own, or a partner's one of the
// organization's connections names (any other is not found, 404). Retiring
// one revokes every link of the organization in it, for good: a revoked link
// stays revoked, and an LMS user is linked again only by their next launch (a
// partner's push never re-links a revoked person). Only a signed-in person with
// the LMS admin scope may; the service says so when this person may not.

// The service answers 50 links a page, so the 20 pages read here see at most
// 1000. When a cursor is still set after them the counts are "at least" and the
// dialog never states an exact number (the typed key is always offered then).
const MAX_PAGES = 20

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

export const UNKNOWN_LMS = 'LMS connection (name unavailable)'

// The sources that have links to remove, built from the account-links list the
// page already reads (no endpoint of its own), grouped by system key, counting
// the links not already revoked. An LMS's own space (lms:...) is named by the
// connection its links came through, or UNKNOWN_LMS when none is known (deleted,
// or the connections did not load); a partner's key is its own name. Exported
// for the tests.
export const sourcesFromLinks = (links, connections) => {
  const names = {}
  ;(connections || []).forEach(connection => {
    names[connection.id] = connection.name
  })
  const bySource = new Map()
  links.forEach(link => {
    if (!link.system_key || link.status === 'revoked') return
    const source = bySource.get(link.system_key) || { key: link.system_key, count: 0, via: [] }
    source.count += 1
    const via = names[link.established_via_connection_id]
    if (via && !source.via.includes(via)) source.via.push(via)
    bySource.set(link.system_key, source)
  })
  return [...bySource.values()]
    .map(source => {
      const partner = !source.key.startsWith('lms:')
      return { ...source, partner, name: partner ? source.key : source.via.length ? source.via.join(', ') : UNKNOWN_LMS }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

const countText = (source, capped) =>
  capped ? `at least ${plural(source.count, 'linked account', 'linked accounts')}` : plural(source.count, 'linked account', 'linked accounts')

// What the warning says will happen. A partner's people are not linked again by
// anything (a partner's push never re-links a revoked person); an LMS's are, by
// their next launch. Either way the learner-group membership goes unless another
// link keeps it. `who` is "N accounts from <name>", or, when no exact number can
// be given, "the accounts linked from <name>".
const warningText = ({ who, partner, org }) =>
  `This unlinks ${who}. They stay members of ${org}, but leave its LMS learner groups unless another link keeps them${
    partner ? '. They won\'t be linked again automatically.' : '; each is linked again the next time they launch from that LMS.'
  } This can't be undone.`

export const RetireSystemKeyModal = ({ connections, connectionsError, onClose, onRetired }) => {
  const authContext = useAuthContext()
  const org = (authContext && authContext.state && authContext.state.organizationName) || 'your organization'

  const [links, updateLinks] = useState(null)
  const [capped, updateCapped] = useState(false)
  const [loadError, updateLoadError] = useState(null)
  const [typed, updateTyped] = useState('')
  const [chosen, updateChosen] = useState('')
  const [error, updateError] = useState(null)
  const [busy, updateBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    const readAll = async () => {
      const all = []
      let cursor = ''
      let more = false
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const result = await lmsAdmin.listLinks({ cursor })
        all.push(...(result.links || []))
        cursor = result.next_cursor || ''
        more = Boolean(cursor)
        if (!cursor) break
      }
      return { all, more }
    }
    readAll()
      .then(({ all, more }) => {
        if (cancelled) return
        updateLinks(all)
        updateCapped(more)
      })
      .catch(e => !cancelled && updateLoadError(e))
    return () => {
      cancelled = true
    }
  }, [])

  // Derived, so the names follow the connections when they arrive after the links.
  const sources = useMemo(() => (links ? sourcesFromLinks(links, connections) : null), [links, connections])
  const picked = useMemo(() => (sources || []).find(source => source.key === chosen) || null, [sources, chosen])

  // The typed key is the only way when the list could not be read, and always an
  // option when it could not be counted in full.
  const typedMode = Boolean(loadError) || capped
  const typedKey = typed.trim()
  const key = typedMode && typedKey ? typedKey : picked ? picked.key : ''
  const keyName = typedMode && typedKey ? typedKey : picked ? picked.name : ''

  const choose = value => {
    updateChosen(value)
    updateTyped('')
  }
  const type = value => {
    updateTyped(value)
    if (value.trim()) updateChosen('')
  }

  const retire = async () => {
    if (!key || busy) return
    updateBusy(true)
    updateError(null)
    try {
      const result = await lmsAdmin.retireSystemKey(key)
      await onRetired(result, keyName)
    } catch (e) {
      updateError(e)
      updateBusy(false)
    }
  }

  let warning = null
  if (typedMode && typedKey) {
    warning = warningText({ who: `the accounts linked from ${typedKey}`, partner: !typedKey.startsWith('lms:'), org })
  } else if (picked) {
    warning = warningText({
      who: capped ? `the accounts linked from ${picked.name}` : `${plural(picked.count, 'account', 'accounts')} from ${picked.name}`,
      partner: picked.partner,
      org
    })
  }

  return (
    <div className="lms-modal-scrim" data-testid="retire-system-key-modal">
      <div className="lms-modal" role="dialog" aria-modal="true" aria-label="Unlink everyone from one LMS">
        <div className="modal-head">
          <div className="icon">
            <LmsIcon name="triangle-exclamation" />
          </div>
          <div className="body">
            <h2>Unlink everyone from one LMS</h2>
            <p className="target">Removes every account link that came from the LMS or partner system you pick</p>
          </div>
          <button className="close-btn" title="Close" type="button" onClick={onClose}>
            <LmsIcon name="xmark" />
          </button>
        </div>

        <div className="lms-modal-body">
          {error && (
            <div className="alert alert-danger" role="alert" data-testid="retire-system-key-error">
              {error.message}
            </div>
          )}

          {connectionsError && (
            <p className="field-help" data-testid="retire-connections-error">
              The connection names couldn't be loaded, so an LMS shows only its key.
            </p>
          )}

          {loadError && (
            <div className="alert alert-warning" role="status">
              {`The list of linked LMSs couldn't be loaded (${loadError.message}). Type the source's key instead.`}
            </div>
          )}

          {!loadError && sources === null && (
            <p className="text-muted" data-testid="retire-sources-loading">Loading the LMSs with linked accounts…</p>
          )}
          {!loadError && sources !== null && sources.length === 0 && (
            <p data-testid="retire-sources-empty">No LMS or partner system has linked accounts to remove.</p>
          )}
          {!loadError && sources !== null && sources.length > 0 && (
            <fieldset className="field" data-testid="retire-sources">
              <legend className="field-label" style={{ float: 'none', width: 'auto', fontSize: 12.5, marginBottom: 6 }}>Choose the LMS or partner system</legend>
              {sources.map(source => (
                <label
                  key={source.key}
                  className={`retire-source${chosen === source.key ? ' selected' : ''}`}
                  style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    name="retire-source"
                    value={source.key}
                    checked={chosen === source.key}
                    data-testid={`retire-source-${source.key}`}
                    onChange={() => choose(source.key)}
                  />
                  <span>
                    {`${source.name} · ${countText(source, capped)}`}
                    <small className="d-block text-muted" style={{ fontFamily: 'var(--edxr-mono)' }}>
                      {source.partner ? 'Partner system' : source.key}
                    </small>
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          {capped && (
            <p className="field-help" data-testid="retire-capped">
              There are more links than this list can count, so the numbers are "at least". If your LMS is not listed, type its key below.
            </p>
          )}

          {typedMode && (
            <div className="field">
              <label className="field-label" htmlFor="retire-system-key-input">
                {loadError ? 'Source key' : 'Or type a source key'}
              </label>
              <input
                id="retire-system-key-input"
                className="form-control form-control-sm"
                type="text"
                placeholder="e.g. acme-hr, or lms:scorm:https://lms.acme.com"
                value={typed}
                data-testid="retire-system-key-input"
                onChange={e => type(e.target.value)}
              />
            </div>
          )}

          {warning && (
            <div className="build-banner danger" style={{ marginTop: 14 }} data-testid="retire-warning">
              <LmsIcon name="triangle-exclamation" />
              <div>{warning}</div>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <span className="help">
            <LmsIcon name="lock" />
            Logged to the audit trail
          </span>
          <button className="btn btn-light border btn-sm" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-danger btn-sm" type="button" disabled={!key || busy} data-testid="retire-system-key-submit" onClick={retire}>
            Unlink everyone
          </button>
        </div>
      </div>
    </div>
  )
}
